/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/**
 * Copyright (c) 2013 MOCA Systems, Inc.  All rights reserved.
 *
 * src/main/resources/com/mocasystems/mb4/ui/ng/ticketspace-app.js
 *
 * Angular top level app module which declares dependencies to other modules.
 */

'use strict';

import * as moment from 'moment';
import * as angular from 'angular';
import firebase from 'firebase/compat/app';

import legacyModule from './legacy-module';

import './vendor/firebase/angularfire';

import { throttleError } from 'utils/basic-utils';

import { analyticsService } from '../plain-javascript/services/analytics/AnalyticsService';
import { sessionErrorTracking } from '../plain-javascript/services/SessionErrorTracking';
import { fbReporting } from '../plain-javascript/background-scripts/fb-reporting';

import { downgradeInjectable, downgradeComponent } from '@angular/upgrade/static';

import { AppComponent } from '../app.component';

import { RouterLink } from '@angular/router';
import { LegacyRouting } from 'src/app/services/legacy-helpers/legacy-routing.service';
import { LegacyHelperService } from 'src/app/services/legacy-helpers/legacy-helper.service';
import { ProjectNotifier } from 'src/app/services/project-notifier.service';
import { ValidatedPlanActions } from 'src/app/services/validated-plan-actions.service';
import { TicketCreatorService } from 'src/app/services/ticket-creator.service';
import { PopupService } from 'src/app/common/popup';
import { AnotherErrorHandler } from 'src/app/services/AnotherErrorHandler';
import { CustomFieldStarterComponent } from 'src/app/modules/custom-field/components/custom-field-starter.component';
import { WideTicketStarterComponent } from '../modules/plan/wide-ticket-modal/wide-ticket-starter.component';
import { AllowedLinksStarterComponent } from '../common/utility-components/allowed-links/allowed-links-starter.component';
import { HoverOverContentStarterComponent } from '../common/utility-components/hover-over-content/hover-over-content-starter.component';
import { FancyCheckboxComponent } from 'src/app/common/utility-components/fancy-checkbox/fancy-checkbox.component';
import { EditWrapperComponent } from 'src/app/modules/ticket-edit/components/edit-wrapper/edit-wrapper.component';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { CanvasViewComponent } from 'src/app/modules/canvas-view/components/canvas-view.component';
// import { PlanComponent } from "ng2/routes/plan.component";
import { ClipboardInDockComponent } from 'src/app/modules/plan/clipboard-in-dock/clipboard-in-dock.component';
import { LoadingComponent } from 'src/app/components/loading-component/loading.component';
import { LegaleseComponent } from 'src/app/common/utility-components/legalese/legalese.component';
import { ReportBlobber } from 'src/app/services/report-blobber.service';
import { FeatureFlagManager } from '../services/FeatureFlagManager';
import { OverviewProjectService } from '../services/overview-project.service';
import { PlanPrintPanelRemoteControlService } from '../services/plan-print-panel-remote-control.service';
import { PlanTicketsFilterRemoteControlService } from '../services/plan-tickets-filter-remote-control.service';
import { CloudStorageService } from 'src/app/services/cloud-storage.service';
import { NavBarPlanComponent } from '../common/utility-components/nav-bar/nav-bar-plan/nav-bar-plan.component';
import { NavBarDashboardComponent } from '../common/utility-components/nav-bar/nav-bar-dashboard/nav-bar-dashboard.component';
import { ZoomControlComponent } from '../common/zoom-control/zoom-control.component';
import { ReportsComponent } from '../modules/reports/components/report/reports.component';
import { MatRoleSelectComponent } from '../shared/ui/material/mat-role-select.component';
import { ProjectDialogLauncher } from '../common/project-dialogs/project-dialog-launcher.service';
import { MatDialog } from '@angular/material/dialog';
import { logger } from '@touchplan/logging';
import { ContextMenuService } from '../modules/ticket-edit/context-menu.service';
import { ProcoreService } from '../services/procore.service';
import { AuthService } from '../services/auth.service';
import { AppState } from '../services/app-state.service';
import { analyticsEventsService } from '../plain-javascript/services/analytics/AnalyticsEventsService';
import { UserPreferenceStateService } from '../services/user-preference-state.service';
import { CrewSizeLauncherComponent } from '../common/utility-components/crew-size-launcher/crew-size-launcher.component';
import { ReportsSwimlaneDataService } from '../modules/reports/services/reports-swimlane-data.service';
import { UserFirebaseCloudFunctionsService } from '../services/firebase-cloud-functions/user-cloud-functions.service';
import { FirebaseCloudFunctionsService } from '../services/firebase-cloud-functions/cloud-functions.service';
import { MatSpinner } from '@angular/material/progress-spinner';
import { PlanAccessCheck } from '../services/PlanAccessCheck';
import { BannerMessageHandler } from '../common/logging/banner-message-handler';
import { PlanPrintService } from 'src/app/modules/plan/services/plan-print.service';
import { TicketEditCommonDesktopService } from '../common/docked-ticket/services/ticket-edit-common-desktop.service';

declare const analytics: any;

const mod = legacyModule;
mod.decorator('$rootScope', [
	'$delegate',
	($delegate) => {
		const originalDigest = $delegate.$digest;
		$delegate.$digest = () => {
			if (!$delegate.$$phase) {
				originalDigest.call($delegate);
			}
		};
		return $delegate;
	},
]);
const appModule: string = mod.name;
export default appModule;
// export {mod as AppModule};
//

angular
	.module('ticketspaceApp')
	.directive('appRoot', downgradeComponent({ component: AppComponent }) as angular.IDirectiveFactory)
	.directive(
		'ticketEditWrapper',
		downgradeComponent({ component: EditWrapperComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'fancyCheckbox',
		downgradeComponent({ component: FancyCheckboxComponent, inputs: ['class'] }) as angular.IDirectiveFactory
	)
	.directive(
		'customFieldStarter',
		downgradeComponent({ component: CustomFieldStarterComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'allowedLinksStarter',
		downgradeComponent({ component: AllowedLinksStarterComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'hoverOverContentStarter',
		downgradeComponent({ component: HoverOverContentStarterComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'crewSizeLauncher',
		downgradeComponent({ component: CrewSizeLauncherComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'wideTicketStarter',
		downgradeComponent({ component: WideTicketStarterComponent }) as angular.IDirectiveFactory
	)
	.directive('routerLink', downgradeComponent({ component: RouterLink }) as angular.IDirectiveFactory)
	.directive(
		'clipboardInDock',
		downgradeComponent({ component: ClipboardInDockComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'angularReportsPanel',
		downgradeComponent({
			component: ReportsComponent,
			inputs: ['roles', 'locations', 'plans', 'customFieldDefinitions', 'swimlanesDropdownData'],
			outputs: ['closing'],
		}) as angular.IDirectiveFactory
	)
	.directive(
		'loadingComponent',
		downgradeComponent({ component: LoadingComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'appZoomControl',
		downgradeComponent({
			component: ZoomControlComponent,
			inputs: ['scale-to-fit', 'hud', 'zoom-power', 'style-class'],
		}) as angular.IDirectiveFactory
	)
	.directive(
		'navBarDashboard',
		downgradeComponent({ component: NavBarDashboardComponent }) as angular.IDirectiveFactory
	)
	.directive(
		'navBarPlan',
		downgradeComponent({ component: NavBarPlanComponent }) as angular.IDirectiveFactory
	)
	.directive('legalese', downgradeComponent({ component: LegaleseComponent }) as angular.IDirectiveFactory)
	.directive(
		'matRoleSelect',
		downgradeComponent({
			component: MatRoleSelectComponent,
			inputs: ['options', 'initialValue', 'multiple'],
			outputs: ['selection'],
		}) as angular.IDirectiveFactory
	)
	.directive('matSpinner', downgradeComponent({ component: MatSpinner }) as angular.IDirectiveFactory)
	.directive('canvasView', downgradeComponent({ component: CanvasViewComponent }))
	.factory('ticketEditService', downgradeInjectable(TicketEditService))
	.factory('ticketEditCommonDesktopService', downgradeInjectable(TicketEditCommonDesktopService))
	.factory('legacyRouting', downgradeInjectable(LegacyRouting))
	.factory('legacyHelperService', downgradeInjectable(LegacyHelperService))
	.factory('reportService', downgradeInjectable(ReportBlobber))
	.factory('projectNotifier', downgradeInjectable(ProjectNotifier))
	.factory('validatedPlanActions', downgradeInjectable(ValidatedPlanActions))
	.factory('ticketCreatorService', downgradeInjectable(TicketCreatorService))
	.factory('popupService', downgradeInjectable(PopupService))
	.factory('anotherErrorHandler', downgradeInjectable(AnotherErrorHandler))
	.factory('featureFlagManager', downgradeInjectable(FeatureFlagManager))
	.factory('overviewProjectService', downgradeInjectable(OverviewProjectService))
	.factory(
		'planTicketsFilterRemoteControlService',
		downgradeInjectable(PlanTicketsFilterRemoteControlService)
	)
	.factory('planPrintPanelRemoteControlService', downgradeInjectable(PlanPrintPanelRemoteControlService))
	.factory('planPrintService', downgradeInjectable(PlanPrintService))
	.factory('matDialog', downgradeInjectable(MatDialog))
	.factory('cloudStorageService', downgradeInjectable(CloudStorageService))
	.factory('projectDialogLauncher', downgradeInjectable(ProjectDialogLauncher))
	.factory('contextMenuService', downgradeInjectable(ContextMenuService))
	.factory('procoreService', downgradeInjectable(ProcoreService))
	.factory('appState', downgradeInjectable(AppState))
	.factory('authService', downgradeInjectable(AuthService))
	.factory('userPreferenceStateService', downgradeInjectable(UserPreferenceStateService))
	.factory('reportsSwimlaneDataService', downgradeInjectable(ReportsSwimlaneDataService))
	.factory('userFirebaseCloudFunctionsService', downgradeInjectable(UserFirebaseCloudFunctionsService))
	.factory('firebaseCloudFunctionsService', downgradeInjectable(FirebaseCloudFunctionsService))
	.factory('planAccessCheck', downgradeInjectable(PlanAccessCheck))
	.factory('bannerMessageHandler', downgradeInjectable(BannerMessageHandler));

//some service imports...
//import "js/services/fb-reporting";

// Enable html5Mode for pushstate ('#'-less URLs)
mod.config([
	'$locationProvider',
	function ($locationProvider) {
		$locationProvider.html5Mode(true);
	},
]);

mod.config([
	'$compileProvider',
	'$animateProvider',
	function ($compileProvider, $animateProvider) {
		//allows dumping of fbdb tables into the page
		$compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|file|data):/);

		$animateProvider.classNameFilter(/animotion/);
	},
]);

mod.constant('logDigests', false);

function mixError(msg) {
	if (analytics) {
		analyticsEventsService.errorClient({ message: JSON.stringify(msg) });
	} else {
		logger.log('not reporting error due to analytics not existing');
	}
}

//add a convienence function to reject with an error when a string is passed in
mod.config([
	'$provide',
	'firebaseRefUrl',
	function ($provide, firebaseRefUrl) {
		$provide.decorator('$q', [
			'$delegate',
			function ($delegate) {
				$delegate.rejectErr = function rejectErr(msg) {
					return this.reject(new Error(msg));
				};
				return $delegate;
			},
		]);
	},
]);

//decorate angularfire
// at this point this is ONLY used by tickets
mod.config([
	'$provide',
	function ($provide) {
		$provide.decorator('$firebaseArray', [
			'$delegate',
			'$firebaseUtils',
			'$q',
			function ($delegate, $firebaseUtils, $q) {
				const _superAdded = $delegate.prototype.$$added;

				$delegate.prototype.$$added = function (snap) {
					const result = _superAdded.call(this, snap);
					const self = this;
					//logger.log('added', snap.key, snap.val());
					if (this.addLag && this.addLag[snap.key]) {
						//logger.log('add lag');
						setTimeout(function () {
							//logger.log('add lagged');
							self.addLag[snap.key].resolve(snap.ref);
							delete self.addLag[snap.key];
						});
					}
					return result;
				};
				$delegate.prototype.$add = function (obj, idKey) {
					const self = this;
					const def = $q.defer();
					const ref = this.$ref().ref.push();
					const outerZone = Zone.current;

					if (idKey) {
						obj[idKey] = ref.key;
					}
					ref.set(
						$firebaseUtils.toJSON(obj),
						$firebaseUtils.runInZone(outerZone, function () {
							logger.log('this one resolved', Zone.current.name);
							if (!self.$getRecord(ref.key)) {
								if (!self.addLag) {
									self.addLag = {};
								}
								self.addLag[ref.key] = $q.defer();
								def.resolve(self.addLag[ref.key].promise);
							} else {
								def.resolve(ref);
							}
						})
					);

					return def.promise;
				};
				return $delegate;
			},
		]);
	},
]);

//section belows displays an error on screen in theory every time one is logged in the console
mod.config([
	'$provide',
	function ($provide) {
		$provide.decorator('$exceptionHandler', [
			'$delegate',
			'anotherErrorHandler',
			function ($delegate, anotherErrorHandler: AnotherErrorHandler) {
				const fakeError = 'An error occurred. Contact Support if you need product help.';
				// logger.log('init angularjs handling');
				const errorThrottle = throttleError(mixError, -10000);

				window.addEventListener('error', function (event) {
					// logger.log('error event listener', event, event.error, event.error.stack, JSON.stringify(event.error.stack));

					const extra = {
						url: null,
						line: null,
						col: null,
						stack: null,
						code: null,
					};
					const errorMessage: string = event.message;
					if (event.filename) {
						extra.url = event.filename;
					}
					if (event.lineno) {
						extra.line = event.lineno;
					}
					if (event.colno) {
						extra.col = event.colno;
					}
					//apparently there's poor support?
					if (event.error) {
						const e = event.error;
						if (e.stack) {
							extra.stack = e.stack;
						}
						if (e.code) {
							extra.code = e.code;
						}
					}
					if ((event as any).extra) {
						Object.assign(extra, (event as any).extra);
					}
					// logger.log('hi', event, extra);

					//list of errors to be explictly ignored
					const errorSuppressionList = ['PERMISSION_DENIED: Permission denied'];
					//list of errors to be explictly ignored, it's also fuzzy
					const fuzzyUISuppressionList = ['SecurityError (DOM Exception 18)'];

					let ignoreError = false;
					const hideError = false;
					for (let i = 0; i < errorSuppressionList.length; i++) {
						if (errorSuppressionList[i] === errorMessage) {
							ignoreError = true;
						}
					}
					for (let i = 0; i < fuzzyUISuppressionList.length; i++) {
						// if(errorMsg.indexOf(fuzzyUISuppressionList[i]) !== -1){hideError = true;}
						if (errorMessage.indexOf(fuzzyUISuppressionList[i]) !== -1) {
							ignoreError = true;
						}
					}

					sessionErrorTracking.pushError(errorMessage);

					if (!ignoreError) {
						// logger.log('extra', extra);
						fbReporting.reportError(errorMessage, extra);
						if (!hideError) {
							Logging.error(fakeError);
						}
						// errorThrottle( errorMessage );
					}
					Logging.debug(errorMessage);
					return false;
				});

				return function (exception, cause) {
					//this is a dumb unhandled rejection error
					if (typeof exception !== 'string') {
						anotherErrorHandler.legacyErrorHandling(exception);
					}
					$delegate(exception, cause);
				};
			},
		]);
	},
]);

mod.run([
	'$rootScope',
	'logDigests',
	function ($rootScope, logDigests) {
		if (logDigests) {
			const $oldDigest = $rootScope.$digest;
			const $newDigest = function () {
				console.time('$digest');
				$oldDigest.apply($rootScope);
				console.timeEnd('$digest');
			};
			$rootScope.$digest = $newDigest;
		}
	},
]);
